/**
 * Storage Abstraction Layer
 * Type-safe wrapper around localStorage with error handling, TTL, and namespacing
 * Last Updated: March 8, 2026
 */

// Note: logger.service imports from storage, so we use console here to avoid circular dependency TDZ

/**
 * Storage item with metadata
 */
interface StorageItem<T> {
  value: T;
  timestamp: number;
  ttl?: number; // Time to live in milliseconds
}

/**
 * Storage options
 */
interface StorageOptions {
  /**
   * Time to live in milliseconds
   * Item will be automatically removed after this duration
   */
  ttl?: number;

  /**
   * Namespace/prefix for the key
   * Helps organize storage keys and avoid collisions
   */
  namespace?: string;

  /**
   * Whether to compress large values (>1KB)
   * Note: Compression not implemented in v1, placeholder for future
   */
  compress?: boolean;
}

/**
 * Storage class with type-safe operations
 */
class Storage {
  private namespace: string = 'sp'; // Default namespace: "sp" for Something Property

  /**
   * Set namespace for all operations
   */
  setNamespace(namespace: string): void {
    this.namespace = namespace;
  }

  /**
   * Build full key with namespace
   */
  private buildKey(key: string, namespace?: string): string {
    const ns = namespace || this.namespace;
    return `${ns}_${key}`;
  }

  /**
   * Check if item is expired
   */
  private isExpired(item: StorageItem<any>): boolean {
    if (!item.ttl) return false;
    const now = Date.now();
    return now - item.timestamp > item.ttl;
  }

  /**
   * Set item in storage
   */
  set<T>(key: string, value: T, options: StorageOptions = {}): boolean {
    try {
      const fullKey = this.buildKey(key, options.namespace);

      const item: StorageItem<T> = {
        value,
        timestamp: Date.now(),
        ttl: options.ttl,
      };

      const serialized = JSON.stringify(item);

      // Check storage quota (5MB limit for most browsers)
      if (serialized.length > 5 * 1024 * 1024) {
        console.warn('[Storage] Storage item too large', {
          key: fullKey,
          size: serialized.length,
        });
        return false;
      }

      localStorage.setItem(fullKey, serialized);

      console.debug('[Storage] Storage item set', {
        key: fullKey,
        size: serialized.length,
        ttl: options.ttl,
      });

      return true;
    } catch (error) {
      console.error('[Storage] Failed to set storage item', error, { key });
      return false;
    }
  }

  /**
   * Get item from storage
   */
  get<T>(key: string, options: StorageOptions = {}): T | null {
    try {
      const fullKey = this.buildKey(key, options.namespace);
      const raw = localStorage.getItem(fullKey);

      if (!raw) return null;

      const item: StorageItem<T> = JSON.parse(raw);

      // Check if expired
      if (this.isExpired(item)) {
        this.remove(key, options);
        console.debug('[Storage] Storage item expired', { key: fullKey });
        return null;
      }

      return item.value;
    } catch (error) {
      console.error('[Storage] Failed to get storage item', error, { key });
      return null;
    }
  }

  /**
   * Remove item from storage
   */
  remove(key: string, options: StorageOptions = {}): void {
    try {
      const fullKey = this.buildKey(key, options.namespace);
      localStorage.removeItem(fullKey);

      console.debug('[Storage] Storage item removed', { key: fullKey });
    } catch (error) {
      console.error('[Storage] Failed to remove storage item', error, { key });
    }
  }

  /**
   * Check if item exists
   */
  has(key: string, options: StorageOptions = {}): boolean {
    const value = this.get(key, options);
    return value !== null;
  }

  /**
   * Clear all items with namespace
   */
  clear(namespace?: string): void {
    try {
      const ns = namespace || this.namespace;
      const prefix = `${ns}_`;

      const keysToRemove: string[] = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach(key => localStorage.removeItem(key));

      console.debug('[Storage] Storage cleared', {
        namespace: ns,
        itemsRemoved: keysToRemove.length,
      });
    } catch (error) {
      console.error('[Storage] Failed to clear storage', error, { namespace });
    }
  }

  /**
   * Get all keys with namespace
   */
  keys(namespace?: string): string[] {
    try {
      const ns = namespace || this.namespace;
      const prefix = `${ns}_`;
      const keys: string[] = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
          // Remove namespace prefix
          keys.push(key.substring(prefix.length));
        }
      }

      return keys;
    } catch (error) {
      console.error('[Storage] Failed to get storage keys', error, { namespace });
      return [];
    }
  }

  /**
   * Get storage size in bytes
   */
  getSize(namespace?: string): number {
    try {
      const ns = namespace || this.namespace;
      const prefix = `${ns}_`;
      let size = 0;

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
          const value = localStorage.getItem(key);
          if (value) {
            size += key.length + value.length;
          }
        }
      }

      return size;
    } catch (error) {
      console.error('[Storage] Failed to get storage size', error, { namespace });
      return 0;
    }
  }

  /**
   * Clean expired items
   */
  cleanExpired(namespace?: string): number {
    try {
      const ns = namespace || this.namespace;
      const prefix = `${ns}_`;
      let cleaned = 0;

      const keysToCheck: string[] = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
          keysToCheck.push(key);
        }
      }

      keysToCheck.forEach(fullKey => {
        try {
          const raw = localStorage.getItem(fullKey);
          if (!raw) return;

          const item: StorageItem<any> = JSON.parse(raw);

          if (this.isExpired(item)) {
            localStorage.removeItem(fullKey);
            cleaned++;
          }
        } catch {
          // JSON.parse failed — item is not a StorageItem (e.g. raw JWT from external-api.ts).
          // Do NOT remove — it belongs to a different storage system (token-storage.ts).
        }
      });

      if (cleaned > 0) {
        console.debug('[Storage] Expired storage items cleaned', {
          namespace: ns,
          itemsCleaned: cleaned,
        });
      }

      return cleaned;
    } catch (error) {
      console.error('[Storage] Failed to clean expired items', error, { namespace });
      return 0;
    }
  }

  /**
   * Migrate old storage key to new format
   */
  migrate(oldKey: string, newKey: string, options: StorageOptions = {}): boolean {
    try {
      // Try to get value with old key (without namespace)
      const raw = localStorage.getItem(oldKey);
      if (!raw) return false;

      // Parse the old value
      let value: any;
      try {
        value = JSON.parse(raw);
      } catch {
        // Not JSON, use as-is
        value = raw;
      }

      // Set with new key and namespace
      const success = this.set(newKey, value, options);

      if (success) {
        // Remove old key
        localStorage.removeItem(oldKey);

        console.info('[Storage] Storage key migrated', {
          oldKey,
          newKey: this.buildKey(newKey, options.namespace),
        });
      }

      return success;
    } catch (error) {
      console.error('[Storage] Failed to migrate storage key', error, { oldKey, newKey });
      return false;
    }
  }

  /**
   * Export all storage data for backup
   */
  export(namespace?: string): Record<string, any> {
    try {
      const ns = namespace || this.namespace;
      const prefix = `${ns}_`;
      const data: Record<string, any> = {};

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
          const value = localStorage.getItem(key);
          if (value) {
            const shortKey = key.substring(prefix.length);
            try {
              data[shortKey] = JSON.parse(value);
            } catch {
              data[shortKey] = value;
            }
          }
        }
      }

      return data;
    } catch (error) {
      console.error('[Storage] Failed to export storage', error, { namespace });
      return {};
    }
  }

  /**
   * Import storage data from backup
   */
  import(data: Record<string, any>, options: StorageOptions = {}): number {
    try {
      let imported = 0;

      Object.entries(data).forEach(([key, value]) => {
        if (this.set(key, value, options)) {
          imported++;
        }
      });

      console.info('[Storage] Storage data imported', {
        itemsImported: imported,
        namespace: options.namespace || this.namespace,
      });

      return imported;
    } catch (error) {
      console.error('[Storage] Failed to import storage', error);
      return 0;
    }
  }
}

/**
 * Singleton storage instance
 */
export const storage = new Storage();

/**
 * Specialized storage for specific features
 */
export const authStorage = {
  set: <T>(key: string, value: T, ttl?: number) =>
    storage.set(key, value, { namespace: 'sp_auth', ttl }),
  get: <T>(key: string) => storage.get<T>(key, { namespace: 'sp_auth' }),
  remove: (key: string) => storage.remove(key, { namespace: 'sp_auth' }),
  clear: () => storage.clear('sp_auth'),
};

export const userStorage = {
  set: <T>(key: string, value: T, ttl?: number) =>
    storage.set(key, value, { namespace: 'sp_user', ttl }),
  get: <T>(key: string) => storage.get<T>(key, { namespace: 'sp_user' }),
  remove: (key: string) => storage.remove(key, { namespace: 'sp_user' }),
  clear: () => storage.clear('sp_user'),
};

export const appStorage = {
  set: <T>(key: string, value: T, ttl?: number) =>
    storage.set(key, value, { namespace: 'sp_app', ttl }),
  get: <T>(key: string) => storage.get<T>(key, { namespace: 'sp_app' }),
  remove: (key: string) => storage.remove(key, { namespace: 'sp_app' }),
  clear: () => storage.clear('sp_app'),
};

export const cacheStorage = {
  set: <T>(key: string, value: T, ttl: number = 5 * 60 * 1000) =>
    storage.set(key, value, { namespace: 'sp_cache', ttl }),
  get: <T>(key: string) => storage.get<T>(key, { namespace: 'sp_cache' }),
  remove: (key: string) => storage.remove(key, { namespace: 'sp_cache' }),
  clear: () => storage.clear('sp_cache'),
};

/**
 * Initialize storage (clean expired items on app start)
 */
export function initStorage(): void {
  try {
    storage.cleanExpired();
    storage.cleanExpired('sp_auth');
    storage.cleanExpired('sp_user');
    storage.cleanExpired('sp_app');
    storage.cleanExpired('sp_cache');

    console.debug('[Storage] Storage initialized');
  } catch (error) {
    console.error('[Storage] Failed to initialize storage', error);
  }
}

// Auto-initialize on module load
if (typeof window !== 'undefined') {
  initStorage();

  // Clean expired items every 5 minutes
  setInterval(() => {
    storage.cleanExpired();
    storage.cleanExpired('sp_auth');
    storage.cleanExpired('sp_user');
    storage.cleanExpired('sp_app');
    storage.cleanExpired('sp_cache');
  }, 5 * 60 * 1000);
}
